import merge from 'deepmerge';
import { isAnyOf } from '@reduxjs/toolkit';

import { baseThunkConfig, createAppSlice } from '@exp/redux/createAppSlice';

import { getUserBrands } from '../api/campaign';
import { postSignIn, postSignUp } from '../api/sign-on';
import { getAuthenticatedUser, postRequestVerification, postVerifyWithCode } from '../api/user';
import { getFeedSummary } from '../api/user-content';
import { getMyTeams } from '../api/user-affiliations';
import { ApplicationStatus, InitialFormModes, TeamType } from '../constants/constants';
import { isAffiliationActive, isApplicationPending } from '../utilities/statuses';
import { filterAffiliations, sanitizeObject } from '../utilities/utilities';
import { attemptGroupCode, fetchApplication, resubmitApplication, submitApplication } from './application';

export const BackdropStage = {
  initial: 'initial',
  manage: 'manage',
  welcome: 'welcome',
  interests: 'interests',
  setup: 'setup',
  qualify: 'qualify',
  postAffiliation: 'postAffiliation',
};

export const BackdropTabs = {
  listing: 'manage-listing',
  qualify: 'manage-qualify',
};

const findAffiliation = (affiliations, { teamId, teamKey }) => {
  if ((!teamId && !teamKey) || !affiliations?.length) {
    // No teamId or teamKey or the user has no affiliations
    return null;
  }

  // Search for any affiliations matching the specified teamId or teamKey
  return affiliations.find(
    (affiliation) => (teamId && +teamId === affiliation.teamId) || (teamKey && teamKey === affiliation.teamKey),
  );
};

const parseAffiliation = ({ affiliation, latestApplication, team }) => {
  const pending = isApplicationPending(latestApplication?.applicationStatus);
  return {
    active: isAffiliationActive(affiliation?.affiliationStatus),
    affiliation,
    affiliationStatus: affiliation?.affiliationStatus,
    draft: latestApplication?.applicationStatus === ApplicationStatus.DRAFT,
    expired: !!affiliation?.expired,
    expiringSoon: !!affiliation?.expiringSoon,
    latestApplication,
    latestApplicationStatus: latestApplication?.applicationStatus,
    location: latestApplication?.location || affiliation?.location,
    pending,
    renewable:
      (affiliation?.expired || affiliation?.expiringSoon) && // Only expired or expiring soon
      !pending && // Not any with a pending application already
      team.applicationsEnabled && // Team must allow applications
      (team.publicApplicationFormId || team.renewalApplicationFormId || team.type === TeamType.RETAIL),
    team,
    teamId: team.teamId,
    teamKey: team.teamKey,
    teamType: team.type,
  };
};

export const Destinations = {
  home: '/home',
  profile: '/user/profile',
};

export const initialState = (config = {}) => ({
  backable: undefined,
  config: sanitizeObject(config), // Sanitize so we only put serializable values in the store
  context: {},
  isHeaderVisible: true,
});

const clearAffCache = (state) => {
  const key = state.context.affCacheKey;
  localStorage.removeItem(key);
};

/**
 * Hand the signed-in identity to core-front-end's ExpUser — `@exp/features` re-decides its flags
 * off the `change` event it emits. Fully optional-chained because core-front-end isn't always
 * loaded (Storybook, tests, standalone embeds), and ExpUser's private fields mean we can't
 * substitute one. Called from the thunk rather than `fulfilled`: `identify` emits synchronously.
 * (TPF-65433)
 */
const identifyUser = (user) => window.Exp?.user?.identify?.(user?.uuid ?? null, { userId: user?.userId });
/**
 * Context Slice
 */
const contextSlice = createAppSlice({
  initialState,
  name: 'context',

  extraReducers: (builder) =>
    builder
      .addCase(attemptGroupCode.fulfilled, (state, action) => {
        state.context.groupCode = action.payload.groupCode;
        state.context.groupCodeResult = action.payload;
        state.context.selectedTeam = action.payload.team;

        if (action.payload.isSubmitted) {
          clearAffCache(state);
          state.context.affiliations = (state.context.affiliations || []).concat([
            parseAffiliation({
              affiliation: action.payload.affiliation,
              latestApplication: action.payload.teamApplication,
              team: action.payload.team,
            }),
          ]);
          state.context.submittedApplication = action.payload;
        }
      })
      .addCase(fetchApplication.fulfilled, (state, action) => {
        const application = action.payload;
        const applicationStatus = application?.applicationStatus;
        if (!applicationStatus || !state.context.submittedApplication) {
          return;
        }

        state.context.submittedApplication = {
          ...state.context.submittedApplication,
          applicationStatus,
          isApproved: applicationStatus === ApplicationStatus.APPROVED,
          isPending: isApplicationPending(applicationStatus),
          teamApplication: application,
        };
      })
      .addMatcher(isAnyOf(submitApplication.fulfilled, resubmitApplication.fulfilled), (state, action) => {
        state.context.submittedApplication = action.payload;

        if (action.payload.isSubmitted) {
          clearAffCache(state);
          // Add the team to the user's affiliation when they submit an application
          state.context.affiliations = (state.context.affiliations || []).concat([
            parseAffiliation({
              affiliation: action.payload.affiliation,
              latestApplication: action.payload.teamApplication,
              team: action.payload.team,
            }),
          ]);
        }
      }),

  reducers: (create) => ({
    fetchAuthenticatedUser: create.asyncThunk(
      async () => {
        const { addressDto, emailDtos, mediaFiles, userDto, userDemographicDto } = await getAuthenticatedUser();
        const emailVerified = emailDtos?.find((e) => e.primaryEmail)?.active;
        return {
          ...userDto,
          birthYear: userDemographicDto?.birthYear,
          birthDate: userDemographicDto?.birthDate,
          country: addressDto?.countryCode,
          emailVerified,
          fullName: `${userDto.firstName || ''} ${userDto.lastName || ''}`.trim(),
          state: addressDto?.state,
          // Prefer the square variation of their avatar
          avatar: mediaFiles?.profileAvatar?.variations?.square || mediaFiles?.profileAvatar?.url || userDto.avatar,
          hero: mediaFiles?.profileHero?.variations?.mobile || mediaFiles?.profileHero?.url,
        };
      },
      {
        ...baseThunkConfig,
        fulfilled: (state, action) => {
          const { addressId, birthDate, birthYear, emailVerified, uuid } = action.payload;
          const ageConfirmed = !!(birthYear || birthDate);
          state.context.user = action.payload;
          state.context.affCacheKey = `ev_user_aff_${uuid}`;
          state.context.emailVerified = emailVerified;
          state.context.ageConfirmed = ageConfirmed;
          // The backend now always assigns a default (GPS-derived) address at signup, so
          // `addressId` alone no longer means the user confirmed a location. If, the first time
          // this ever resolves, neither email nor birthday is done yet, ignore that default until
          // the location section is actually opened (see clearIgnoreDefaultLocationOnce).
          if (state.context.ignoreDefaultLocationOnce === undefined) {
            state.context.ignoreDefaultLocationOnce = !emailVerified && !ageConfirmed;
          }
          state.context.locationConfirmed = !!addressId && !state.context.ignoreDefaultLocationOnce;
        },
      },
    ),
    fetchUserAffiliations: create.asyncThunk(
      async (forceRefresh) => {
        const data = await getMyTeams(forceRefresh);
        return data.map(parseAffiliation);
      },
      {
        ...baseThunkConfig,
        fulfilled: (state, action) => {
          clearAffCache(state);
          state.context.affiliations = action.payload;
        },
      },
    ),

    fetchUserBrands: create.asyncThunk(() => getUserBrands(), {
      ...baseThunkConfig,
      fulfilled: (state, action) => {
        state.context.userBrands = action.payload;
      },
    }),

    signIn: create.asyncThunk(
      async (data) => {
        const result = await postSignIn({
          identifier: data.username?.trim(),
          password: data.password,
        });
        identifyUser(result.user);
        return result;
      },
      {
        ...baseThunkConfig,
        fulfilled: (state, action) => {
          state.context.user = action.payload.user;
        },
      },
    ),

    signUp: create.asyncThunk(
      async (data) => {
        const result = await postSignUp({
          ageConfirmation: data.ageConfirmation,
          contact: data.contact,
          countryCode: data.countryCode,
          email: data.username?.trim(),
          firstName: data.firstName,
          lastName: data.lastName,
          memberId: data.memberId,
          password: data.password,
          state: data.state,
          teamTargetId: data.teamTargetId,
          terms: data.terms,
        });
        identifyUser(result.user);
        return result;
      },
      {
        ...baseThunkConfig,
        fulfilled: (state, action) => {
          state.context.createdAffiliations = action.payload.createdAffiliations;
          state.context.potentialAffiliations = action.payload.potentialAffiliations;
          state.context.user = action.payload.user;
        },
      },
    ),

    fetchFeedSummary: create.asyncThunk(() => getFeedSummary(), {
      ...baseThunkConfig,
      fulfilled: (state, action) => {
        state.feedSummary = action.payload;
      },
    }),

    focusQuestion: (state, action) => {
      state.focusedQuestion = action.payload;
    },
    requestVerificationCode: create.asyncThunk(({ email }) => postRequestVerification(email), {
      ...baseThunkConfig,
      fulfilled: (state) => {
        state.context = { ...state.context, emailVerificationRejected: null, emailVerified: false };
      },
      rejected: (state) => {
        state.context = { ...state.context, emailVerificationRejected: null, emailVerified: false };
      },
    }),
    clearBackdrop: (state) => {
      state.backdropStage = undefined;
    },
    setBackdrop: (state, action) => {
      state.backdropStage = action.payload;
    },
    setBackdropTab: (state, action) => {
      state.backdropTab = action.payload;
    },
    setFormMode: (state, action) => {
      state.formMode = action.payload;
    },
    setConfig: (state, action) => {
      state.config = merge(state.config, action.payload);
    },
    setContext: (state, action) => {
      state.context = merge(state.context, action.payload, {
        arrayMerge: (dest, src) => src, // Stomp arrays rather than combine them
      });
    },
    setEmailVerificationRejected: (state) => {
      state.context = { ...state.context, emailVerificationRejected: null, emailVerified: false };
    },
    setPageOverviews: (state, action) => {
      state.overviews = action.payload;
    },
    submitVerificationCode: create.asyncThunk(
      async ({ email, verificationCode }) => {
        await postVerifyWithCode(email, verificationCode);
      },
      {
        ...baseThunkConfig,
        pending: (state) => {
          state.context = { ...state.context, emailVerificationRejected: null };
        },
        fulfilled: (state) => {
          state.context = { ...state.context, emailVerificationRejected: null, emailVerified: true };
        },
        rejected: (state, action) => {
          state.context = {
            ...state.context,
            emailVerificationRejected: parseInt(action.error?.status, 10),
            emailVerified: false,
          };
        },
      },
    ),
    resetPageState: (state) => {
      state.isBackable = undefined;
      state.isHeaderVisible = true;
    },
    clearIgnoreDefaultLocationOnce: (state) => {
      state.context.ignoreDefaultLocationOnce = false;
    },
    setAgeConfirmed: (state, action) => {
      state.context.ageConfirmed = action.payload;
    },
    setHowToComplete: (state, action) => {
      state.context.howToComplete = action.payload;
    },
    setLocationConfirmed: (state, action) => {
      state.context.locationConfirmed = action.payload;
    },
    setMatchMode: (state, action) => {
      state.context.matchMode = action.payload;
    },
    setNameConfirmed: (state, action) => {
      state.context.nameConfirmed = action.payload;
    },
    setPersonalizationComplete: (state, action) => {
      state.context.personalizationComplete = action.payload;
    },
    toggleBackable: (state, action) => {
      state.isBackable = action.payload;
    },
    toggleHeaderVisible: (state, action) => {
      state.isHeaderVisible = action.payload !== undefined ? action.payload : !state.isHeaderVisible;
    },

    updateQuestions: (state, action) => {
      // Reset the questions in the application form & the provided answers
      state.context.pendingApplication.applicationForm.questions = action.payload;
      state.context.pendingApplication.questions = {};
    },
  }),
  selectors: {
    selectAffiliation: (state, team = null) => {
      const { affiliations, selectedTeam } = contextSlice.getSelectors().selectContext(state);
      return findAffiliation(affiliations, team || selectedTeam || {});
    },
    selectBackdrop: (state) => state.backdropStage,
    selectBackdropTab: (state) => state.backdropTab,
    /**
     * Whether the manage backdrop — the only thing that renders the My Teams /
     * Explore All Teams tabs and the affiliation listing — is on screen. Both
     * `Backdrop` and `StaticLayoutV3` read this so the active `backdropTab` can
     * never describe a tab strip that was never rendered (TPF-65908).
     */
    selectShowManageBackdrop: (state) => {
      const { affiliations } = contextSlice.getSelectors().selectContext(state);
      const stage = contextSlice.getSelectors().selectBackdrop(state);
      return (
        stage === BackdropStage.manage ||
        (stage === BackdropStage.qualify && !!affiliations?.filter(filterAffiliations).length)
      );
    },
    selectConfig: (state) => state.config,
    selectContext: (state) => state.context,
    selectDestination: (state) => {
      const { initialFormMode } = contextSlice.getSelectors().selectContext(state);
      const { allowAuthenticated, destination, expectAuthenticated, signInDestination, signUpDestination } =
        contextSlice.getSelectors().selectConfig(state);

      // Determine the destination, prefer the configured destination, fall back to the one matching the initial form mode
      let target = destination;
      if (!target) {
        switch (initialFormMode) {
          case InitialFormModes.PRE_AUTHENTICATED:
            if (!allowAuthenticated && !expectAuthenticated) {
              // Use the sign in destination if we're not allowing an authenticated user
              target = signInDestination;
            }
            break;
          case InitialFormModes.SIGN_IN:
            target = signInDestination;
            break;
          case InitialFormModes.SIGN_UP:
            target = signUpDestination;
            break;
          default:
            break;
        }
      }

      // Convert the keyword destinations to their actual URLs
      return Destinations[target] || target;
    },
    selectFocusedQuestion: (state) => state.focusedQuestion,
    selectFormMode: (state) => state.formMode,
    selectIsBackable: (state) => state.isBackable,
    selectIsHeaderVisible: (state) => state.isHeaderVisible,
    selectIsDestinationProfile: (state) => {
      const destination = contextSlice.getSelectors().selectDestination(state);
      return Destinations.profile === destination;
    },
    selectPageOverviews: (state) => state.overviews,
    selectUser: (state) => state.user,
    // A primitive on purpose: useSendEvent subscribes to this from ~60 call sites, and selecting the
    // whole context object there would re-render all of them on every unrelated context change.
    selectUserUuid: (state) => state.context.user?.uuid,
    selectDocumentVerificationResults: (state) => {
      const context = contextSlice.getSelectors().selectContext(state);
      return context.submittedApplication?.documentVerificationResults;
    },
  },
});

export const {
  clearBackdrop,
  clearIgnoreDefaultLocationOnce,
  fetchAuthenticatedUser,
  fetchFeedSummary,
  fetchUserAffiliations,
  fetchUserBrands,
  focusQuestion,
  resetPageState,
  requestVerificationCode,
  setAgeConfirmed,
  setBackdrop,
  setBackdropTab,
  setConfig,
  setContext,
  setEmailVerificationRejected,
  setFormMode,
  setHowToComplete,
  setLocationConfirmed,
  setNameConfirmed,
  setMatchMode,
  setPageOverviews,
  setPersonalizationComplete,
  signIn,
  signUp,
  submitVerificationCode,
  toggleBackable,
  toggleHeaderVisible,
  updateQuestions,
} = contextSlice.actions;

export const {
  selectAffiliation,
  selectBackdrop,
  selectBackdropTab,
  selectConfig,
  selectContext,
  selectDestination,
  selectDocumentVerificationResults,
  selectFocusedQuestion,
  selectFormMode,
  selectIsBackable,
  selectIsDestinationProfile,
  selectIsHeaderVisible,
  selectPageOverviews,
  selectShowManageBackdrop,
  selectUser,
  selectUserUuid,
} = contextSlice.selectors;

export default contextSlice;
